import React, { useCallback, useState } from "react";
import { Typography } from "../Typography";
import styled from "@emotion/styled";
import { Button as BaseButton } from "../Button";
import InfoIcon from "@ecologi/assets/icons/Status/icon_information.svg";
import WarningIcon from "@ecologi/assets/icons/Status/icon_warning.svg";
import CriticalIcon from "@ecologi/assets/icons/Status/icon_critical.svg";
import CloseIconGraphic from "@ecologi/assets/icons/close-icon.svg";
import { Action } from "../BadgeCard";

const Wrapper = styled.div<{ status: string }>`
  padding: 3.2rem 1.6rem;
  margin-bottom: 20px;
  background-color: ${(props) => props.theme.colors.mushroomLight};
  display: flex;
  overflow: auto;
`;

const CloseIconContainer = styled.button`
  appearance: none;
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  font-size: inherit;
  line-height: inherit;
  cursor: pointer;
  position: relative;
  margin-left: 2rem;
  margin-right: -0.8rem;
  padding: 0.8rem;
  border-radius: 6px;
  outline: none;
  height: 100%;
`;

const CloseIcon = styled(CloseIconGraphic)`
  display: block;
  height: 2rem;
  width: 2rem;
  max-height: 100%;
  max-width: 100%;
  margin: auto;
  fill: ${(props) => props.theme.colors.charcoal};
`;

const Button = styled(BaseButton)`
  margin-top: 20px;
  /* width: 100%; */

  @media ${(props) => props.theme.bp.mobileUp} {
    width: auto;
  }
`;

const SecondaryButton = styled(Button)`
  text-decoration: underline;
  background-color: transparent;
  font-family: "Sofia Pro";
  border: none;
  cursor: pointer;
  text-align: start;
  white-space: nowrap;
  justify-content: start;
  padding-left: 0;

  @media ${(props) => props.theme.bp.mobileUp} {
    margin-left: 20px;
  }
`;

const Icon = styled.div`
  padding-top: 5px;
  padding-right: 20px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;

  @media ${(props) => props.theme.bp.mobileUp} {
    flex-direction: row;
  }
`;

const Inner = styled.div`
  /* width: 100%; */
`;

export enum BannerStatuses {
  INFO = "info",
  WARNING = "warning",
  CRITICAL = "critical",
  DEFAULT = "default",
}

const iconMapper = {
  [BannerStatuses.INFO]: <InfoIcon />,
  [BannerStatuses.WARNING]: <WarningIcon />,
  [BannerStatuses.CRITICAL]: <CriticalIcon />,
  [BannerStatuses.DEFAULT]: <InfoIcon />,
};

export type Action = {
  content: string;
  onAction: () => unknown | Promise<unknown>;
  loading?: boolean;
};

export type InlineBannerProps = {
  status: string;
  title: string;
  action?: Action;
  secondary?: Action;
  children?: React.ReactNode;
  onDismiss?: () => void;
};

// const BackgroundColorMapper = {
//   [BannerStatuses.INFO]: "#eef9f9",
//   [BannerStatuses.WARNING]: "#fff5ea",
//   [BannerStatuses.CRITICAL]: "#fff4f4",
//   [BannerStatuses.DEFAULT]: "#eef9f9",
// };

// const BorderTopMapper = {
//   [BannerStatuses.INFO]:
//     "inset 0 3px 0 0 #47c1bf, inset 0 0 0 0 transparent, 0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)",
//   [BannerStatuses.WARNING]:
//     "inset 0 3px 0 0 #b98900, inset 0 0 0 0 transparent, 0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)",
//   [BannerStatuses.CRITICAL]:
//     "inset 0 3px 0 0 #fd5749, inset 0 0 0 0 transparent, 0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)",
//   [BannerStatuses.DEFAULT]:
//     "inset 0 3px 0 0 #47c1bf, inset 0 0 0 0 transparent, 0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)",
// };

export const InlineBanner = ({
  title,
  status = "default",
  children,
  action,
  secondary,
  onDismiss,
}: InlineBannerProps) => {
  const [isClosed, setIsClosed] = useState(false);

  // Use Promise states to set loading / disabled status
  const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
  const performPrimaryAction = useCallback(async () => {
    setIsPrimaryLoading(true);
    try {
      await action?.onAction();
    } finally {
      setIsPrimaryLoading(false);
    }
  }, [action]);

  const [isSecondaryLoading, setIsSecondaryLoading] = useState(false);
  const performSecondaryAction = useCallback(async () => {
    setIsSecondaryLoading(true);
    try {
      await secondary?.onAction();
    } finally {
      setIsSecondaryLoading(false);
    }
  }, [secondary]);

  return (
    <>
      {!isClosed && (
        <Wrapper status={status}>
          <Icon>{iconMapper[status]}</Icon>
          <Inner>
            <Typography use="body2" as="h3">
              {title}
            </Typography>
            <Typography use="body1" as="p">
              {children}
            </Typography>
            <ButtonWrapper>
              {action && (
                <Button
                  darkGreen
                  small
                  text={action.content}
                  onClick={performPrimaryAction}
                  loading={action.loading || isPrimaryLoading}
                  disabled={action.loading || isPrimaryLoading}
                />
              )}
              {secondary && (
                <SecondaryButton
                  textOnly
                  small
                  text={secondary.content}
                  onClick={performSecondaryAction}
                  loading={secondary.loading || isSecondaryLoading}
                  disabled={secondary.loading || isSecondaryLoading}
                />
              )}
            </ButtonWrapper>
          </Inner>
          {onDismiss && (
            <CloseIconContainer onClick={() => setIsClosed(true)}>
              <CloseIcon />
            </CloseIconContainer>
          )}
        </Wrapper>
      )}
    </>
  );
};
